// ProjectSelectorModal — Select a project to link a directory entity to
import { useState, useEffect } from 'react'
import { X, Search, Loader2, CheckCircle, AlertCircle, FolderOpen } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { DirectoryEntity, Project } from '../../types'
import { getProjects, copyToProject } from '../../services/api'
import { disambiguateProjectLabels } from '../../utils/projects'

interface ProjectSelectorModalProps {
  isOpen: boolean
  onClose: () => void
  entity: DirectoryEntity
  onLinked?: (projectId: string) => void
}

export default function ProjectSelectorModal({ isOpen, onClose, entity, onLinked }: ProjectSelectorModalProps) {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [linking, setLinking] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ projectId: string; projectName: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setLoading(true)
    setSuccess(null)
    setError(null)
    setSearch('')
    getProjects()
      .then(setProjects)
      .catch(() => setProjects([]))
      .finally(() => setLoading(false))
  }, [isOpen])

  const handleLink = async (project: Project) => {
    setLinking(project.project_id)
    setError(null)
    try {
      await copyToProject(entity.entity_id, project.project_id)
      setSuccess({ projectId: project.project_id, projectName: project.name })
      onLinked?.(project.project_id)
    } catch {
      setError(`Failed to link to "${project.name}". Please try again.`)
    } finally {
      setLinking(null)
    }
  }

  if (!isOpen) return null

  const disambiguated = disambiguateProjectLabels(projects)
  const filtered = search
    ? disambiguated.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
    : disambiguated

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Link to Project</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Link <span className="font-medium">{entity.canonical_name}</span> to a project
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success state */}
        {success ? (
          <div className="p-6 text-center">
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Successfully Linked</h4>
            <p className="text-xs text-gray-500 mb-4">
              {entity.canonical_name} has been linked to <span className="font-medium">{success.projectName}</span>
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => navigate(`/project/${success.projectId}?tab=collaboration`)}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors"
              >
                View Collaboration Tab
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Search */}
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Error banner */}
            {error && (
              <div className="mx-3 mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                <span className="text-xs text-red-600 flex-1">{error}</span>
              </div>
            )}

            {/* Project list */}
            <div className="flex-1 overflow-y-auto p-3">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading projects...
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {search ? 'No projects match your search.' : 'No projects found.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filtered.map((project) => (
                    <button
                      key={project.project_id}
                      onClick={() => handleLink(project)}
                      disabled={linking !== null}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-teal-50 transition-colors text-left disabled:opacity-50"
                    >
                      <FolderOpen className="w-5 h-5 text-gray-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{project.displayName}</div>
                        <div className="text-xs text-gray-500">
                          {project.document_count} documents · {project.collaborator_count} collaborators
                        </div>
                      </div>
                      {linking === project.project_id && (
                        <Loader2 className="w-4 h-4 animate-spin text-teal-500 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
